import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { buildDrill, cellText, compactDash, croCount, fontForColumn, inr, NUMERIC_COLUMNS, pct, projectColor, sumRows, THEME } from '../utils/trackerConfig';
import { textFits, useFontsReady } from '../utils/textFit';
import CroList, { listCellPx, LIST_VALUE_PX } from './CroList';
import TarPill from './TarPill';
import { useElementSize } from '../../departmentIncomeTracker/utils/layoutHooks';
import DrillValue from './DrillValue';

const HEAD_H = 44; // fixed so the totals row can stick right under the header
const SL_W = 36;
const TEAM_W = 120;
const PROJECT_W = 100;
// The grid always fits the width it is given - it never scrolls sideways (user: "it should not be horizontally scrollable"). The fixed
// columns (#, CRO and the optional Team / Project) take what they need and the number columns share the rest equally. When the box is
// short on width, Team and Project move into the CRO cell as a caption, the CRO column narrows and the type gets a little smaller.
// Below this width seven number columns cannot hold readable numbers, so the grid becomes a list of small cards (CroList) - same rows, same
// order, same numbers, same click-through. Wider than this it is the table.
const LIST_MODE_BELOW = 540;
const TEAM_COL_MIN_WIDTH = 980; // the Team column ("Rank everyone") only when there is room for it
const PROJECT_COL_MIN_WIDTH = 840;
const nameWidth = (w) => (w !== null && w < 480 ? 92 : w !== null && w < 700 ? 112 : w !== null && w < 900 ? 130 : 184);
const slWidth = (w) => (w !== null && w < 480 ? 26 : SL_W);

const gridSx = (fontSize) => ({
  borderCollapse: 'separate',
  tableLayout: 'fixed',
  width: '100%',
  fontVariantNumeric: 'tabular-nums',
  '& th, & td': { borderRight: `1px solid ${THEME.grid}`, borderBottom: `1px solid ${THEME.grid}`, fontSize, px: 0.75 },
});
const headCell = {
  height: HEAD_H,
  fontWeight: 700,
  color: '#fff',
  backgroundColor: THEME.primary,
  // header labels may wrap onto a second line ("Sales Revenue (₹)") so the numeric columns can stay narrow
  whiteSpace: 'normal',
  lineHeight: 1.15,
  py: 0,
  borderRight: '1px solid rgba(255,255,255,0.18) !important',
};
// the sort arrow is shown only on the sorted column and hangs beside the label (it does not take width from the column)
const sortLabelSx = {
  color: '#fff !important',
  position: 'relative',
  // the arrow sits inside the cell (the sorted label keeps room for it) so nothing overhangs the column
  '& .MuiTableSortLabel-icon': { color: '#fff !important', opacity: 0.55, position: 'absolute', right: 0, margin: 0, fontSize: 16 },
  '&.Mui-active': { paddingRight: '16px' },
  '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
};
const stickySl = { position: 'sticky', left: 0 };
const stickyName = { position: 'sticky', boxShadow: `2px 0 0 ${THEME.primaryLine}`, whiteSpace: 'normal', wordBreak: 'break-word' };

/**
 * The CRO table, built the way the sheet behaves - frozen Sl No + CRO columns, a frozen totals row
 * right under the header, click-to-sort headers, quiet zeros - in colour: indigo header, a coloured
 * line under each money column, colour-coded revenue figures, a project-coloured edge on every
 * CRO, and amounts centred in their columns.
 * `rows` arrive filtered AND sorted (the page owns sorting so Copy/Export match what is on screen);
 * `hideTargets` drops the monthly Target/TAR columns (week view).
 */
export default function CroGrid({
  rows,
  groups,
  collapsed,
  onToggleGroup,
  sort,
  onSort,
  showProject,
  hideTargets,
  loading,
  emptyHint,
  onDrill,
  totalScope,
  totalWho,
  expanded,
  maxHeight,
}) {
  // in the flat "rank everyone" view there are no team bands, so each CRO carries their team (in a column when there is room, else under the name)
  const showTeam = !groups;
  const [boxRef, , boxW] = useElementSize();
  useFontsReady();
  const teamCol = showTeam && (boxW === null || boxW >= TEAM_COL_MIN_WIDTH);
  const projectCol = showProject && (boxW === null || boxW >= PROJECT_COL_MIN_WIDTH);
  // what the hidden columns would have said, kept as a caption under the CRO's name
  const captionOf = (c) =>
    [showProject && !projectCol ? c.project : null, showTeam && !teamCol ? (c.teamId === 0 ? 'No CRM' : c.teamName) : null].filter(Boolean).join(' · ');
  const columns = useMemo(() => NUMERIC_COLUMNS.filter((c) => !(hideTargets && c.target)), [hideTargets]);
  const total = useMemo(() => sumRows(rows), [rows]);
  // What the seven number columns can carry. The grid works out how wide a column is, then picks the biggest type that fits and - only when
  // even the full amount in that type would not fit - writes amounts in lakh / crore (and drops the TAR's decimal, and uses the short headers).
  const columnPx = boxW === null ? 120 : (boxW - slWidth(boxW) - nameWidth(boxW) - (teamCol ? TEAM_W : 0) - (projectCol ? PROJECT_W : 0)) / columns.length;
  // the longest amounts in the table are the Total row's, so those are what has to fit - measured with the page's real font
  const amountCols = columns.filter((c) => c.kind === 'money' || c.kind === 'count');
  const fullTexts = amountCols.map((c) => inr(total[c.key]));
  const shortTexts = ['9.99L', ...amountCols.map((c) => compactDash(total[c.key]))];
  // The table stays a table on every laptop. The rows become cards only on a phone (below LIST_MODE_BELOW) or when even the SHORT form of an
  // amount (5.24L) cannot fit its column at the smallest type - a very narrow box or a huge zoom.
  const listMode = boxW !== null && (boxW < LIST_MODE_BELOW || !textFits(shortTexts, columnPx, 10, true, 12));
  // A list card shows its numbers 4, 3 or 2 across - as many as keep every FULL amount whole at full size (so a phone shows complete amounts);
  // the table shows them in `columnPx`-wide columns. Only if even 2 across cannot hold an amount is it shortened.
  const listPerRow = listMode ? [4, 3, 2].filter((n) => n <= columns.length).find((n) => textFits(fullTexts, listCellPx(boxW, n), LIST_VALUE_PX, true, 22)) || 2 : 0; // 22px of air left beside the widest amount

  const cellPx = listMode ? listCellPx(boxW, listPerRow) : columnPx;
  // The type steps DOWN (to 11px) before any amount is shortened: the biggest size at which every full amount fits its column. Only when even
  // 11px cannot hold them are amounts written in lakh / crore (the full amount stays on hover) and the type is the column's own size.
  const fullFont = listMode ? null : [13, 12, 11.5, 11].find((f) => f <= fontForColumn(columnPx) && textFits(fullTexts, cellPx, f, true, 20));
  const compact = listMode ? !textFits(fullTexts, cellPx, LIST_VALUE_PX, true, 4) : !fullFont;
  const fontSize = fullFont || fontForColumn(columnPx);
  const tight = columnPx < 76; // short headers
  // the TAR keeps its decimal (603.4%) whenever the longest one fits its pill, else it is written 603%
  const tarTexts = [total.tar, ...rows.map((r) => r.tar)].filter((v) => v !== null && v !== undefined).map((v) => pct(v, 1));
  const longestTar = tarTexts.sort((a, b) => b.length - a.length)[0];
  const tarDecimals = !longestTar || textFits(longestTar, cellPx, listMode ? LIST_VALUE_PX : fontSize, true, 28) ? 1 : 0;
  const text = (col, row) => cellText(col, row, { compact, tarDecimals });
  const sl = slWidth(boxW);

  const sortHead = (key, label, tip) => (
    <Tooltip title={tip || ''} arrow placement="top" disableHoverListener={!tip}>
      <TableSortLabel
        active={sort.key === key}
        hideSortIcon
        direction={sort.key === key ? sort.dir : 'asc'}
        onClick={() => onSort(key)}
        sx={sortLabelSx}
      >
        {label}
      </TableSortLabel>
    </Tooltip>
  );

  // Every number is a link to the rows behind it. `scope` says which rows: a CRO, a team, or the
  // whole visible table. A zero has nothing behind it, so it stays plain text.
  const cellValue = (col, row, scope, who) => {
    const node = col.kind === 'tar' ? <TarPill dense decimals={tarDecimals} value={row.tar} /> : text(col, row);
    const empty = col.kind === 'tar' ? row.tar === null || row.tar === undefined : !Number(row[col.key]);
    // a shortened amount (5.24L) keeps its full figure on hover / long press
    const full = compact && col.kind !== 'tar' && Number(row[col.key]) >= 1e5 ? `₹ ${inr(row[col.key])}` : null;
    if (!onDrill || empty) return full ? <span title={full}>{node}</span> : node;
    return (
      <DrillValue tip={full ? `${full} — click to see the rows behind this number` : undefined} onClick={() => onDrill(buildDrill(col, row, scope, who))}>
        {node}
      </DrillValue>
    );
  };

  const colSpan = columns.length + 2 + (projectCol ? 1 : 0) + (teamCol ? 1 : 0);
  const totalBg = THEME.total; // the Accounts Manager's total-row grey

  return (
    <Box ref={boxRef} sx={{ position: 'relative' }}>
      {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 6, height: 3 }} />}
      {listMode ? (
        <CroList
          columns={columns}
          rows={rows}
          groups={groups}
          collapsed={collapsed}
          onToggleGroup={onToggleGroup}
          sort={sort}
          onSort={onSort}
          showProject={showProject}
          loading={loading}
          emptyHint={emptyHint}
          total={total}
          totalScope={totalScope}
          totalWho={totalWho}
          renderValue={cellValue}
          perRow={listPerRow}
          expanded={expanded}
          maxHeight={maxHeight}
        />
      ) : (
      <TableContainer
        sx={{
          // expanded = no inner scroll: the table grows to show every row and the page scrolls instead
          maxHeight: expanded ? 'none' : maxHeight || 'calc(100vh - 390px)',
          // never sideways; when expanded the box is not a scroller of its own (clip), so the header and total rows follow the PAGE scroll
          overflowX: expanded ? 'clip' : 'hidden',
          minHeight: 220,
          border: `1px solid ${THEME.grid}`,
          borderRadius: '12px',
          backgroundColor: 'white',
          boxShadow: '0 1px 6px rgba(15,23,42,0.10)',
          opacity: loading ? 0.6 : 1,
          transition: 'opacity .15s',
        }}
      >
        <Table size="small" stickyHeader sx={gridSx(fontSize)}>
          <colgroup>
            <col style={{ width: sl }} />
            <col style={{ width: nameWidth(boxW) }} />
            {teamCol && <col style={{ width: TEAM_W }} />}
            {projectCol && <col style={{ width: PROJECT_W }} />}
            {columns.map((c) => (
              <col key={c.key} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCell, ...stickySl, zIndex: 5 }}>#</TableCell>
              <TableCell sx={{ ...headCell, ...stickyName, left: sl, zIndex: 5 }}>{sortHead('name', 'CRO', groups ? 'Sorts teams by their total and the CROs inside each team. Switch to "Rank everyone" to sort all CROs together.' : 'Click any header to rank all CROs together')}</TableCell>
              {teamCol && (
                <TableCell align="center" sx={headCell}>
                  {sortHead('team', 'Team (CRM)', 'Sort all CROs by their CRM')}
                </TableCell>
              )}
              {projectCol && (
                <TableCell align="center" sx={headCell}>
                  {sortHead('project', 'Project')}
                </TableCell>
              )}
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align="center"
                  sx={headCell}
                >
                  {sortHead(c.key, tight ? c.short : c.label, c.tip ? `${c.label}: ${c.tip}` : c.label)}
                </TableCell>
              ))}
            </TableRow>
            {/* totals frozen right under the headers - the sheet keeps its Total in row 9 */}
            <TableRow>
              <TableCell sx={{ ...stickySl, top: HEAD_H, zIndex: 4, backgroundColor: totalBg }} />
              <TableCell sx={{ ...stickyName, left: sl, top: HEAD_H, zIndex: 4, backgroundColor: totalBg, fontWeight: 800, color: THEME.primaryDark }}>
                Total · {croCount(rows)} {croCount(rows) === 1 ? 'CRO' : 'CROs'}
              </TableCell>
              {teamCol && <TableCell sx={{ position: 'sticky', top: HEAD_H, zIndex: 3, backgroundColor: totalBg }} />}
              {projectCol && <TableCell sx={{ position: 'sticky', top: HEAD_H, zIndex: 3, backgroundColor: totalBg }} />}
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align="center"
                  sx={{ position: 'sticky', top: HEAD_H, zIndex: 3, backgroundColor: totalBg, fontWeight: 800, color: c.color }}
                >
                  {cellValue(c, total, totalScope, totalWho || 'All CROs')}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">{emptyHint || 'No CROs match these filters.'}</Typography>
                </TableCell>
              </TableRow>
            )}
            {(() => {
              let n = 0; // running # across every visible CRO; team bands and a CRM's own line do not count
              const croRow = (c) => {
                const i = n;
                if (!c.isCrm) n += 1;
                const pc = projectColor(c.projectKey);
                return (
                  <TableRow
                    key={`${c.userId}|${c.projectKey}`}
                    hover
                    sx={{
                      backgroundColor: c.isCrm ? THEME.primarySoft : '#fff', // plain white rows (no stripes); a CRM's own line is light blue
                      '&:hover': { backgroundColor: `${THEME.rowHover} !important` },
                    }}
                  >
                    <TableCell align="center" sx={{ ...stickySl, backgroundColor: 'inherit', color: '#94A3B8', fontWeight: 600 }}>
                      {c.isCrm ? '•' : i + 1}
                    </TableCell>
                    <TableCell
                      sx={{ ...stickyName, left: sl, backgroundColor: 'inherit', fontWeight: 600, color: '#1E293B', borderLeft: `4px solid ${pc.main}`, pl: groups ? 3 : 2 }}
                    >
                      {c.name}
                      {c.isCrm && (
                        <Tooltip title="The CRM's own income, sales and target - counted in the team total" arrow>
                          <Chip label="CRM · own" size="small" sx={{ ml: 1, height: 18, fontSize: 11, fontWeight: 700, color: '#5B21B6', backgroundColor: '#DDD6FE' }} />
                        </Tooltip>
                      )}
                      {!c.active && (
                        <Tooltip title="Not an active employee today, but they brought income in this period" arrow>
                          <Chip
                            label="Inactive"
                            size="small"
                            sx={{ ml: 1, height: 18, fontSize: 11, fontWeight: 600, color: '#9A3412', backgroundColor: '#FFEDD5' }}
                          />
                        </Tooltip>
                      )}
                      {captionOf(c) && (
                        <Box component="div" sx={{ fontSize: 11, fontWeight: 500, color: pc.text, lineHeight: 1.25 }}>
                          {captionOf(c)}
                        </Box>
                      )}
                    </TableCell>
                    {teamCol && (
                      <TableCell align="center" sx={{ fontWeight: 600, color: pc.text }}>
                        {c.teamId === 0 ? <span style={{ color: '#94A3B8' }}>No CRM</span> : c.teamName}
                      </TableCell>
                    )}
                    {projectCol && (
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Chip label={c.project} size="small" sx={{ height: 22, fontWeight: 700, color: pc.text, backgroundColor: pc.soft, '& .MuiChip-label': { px: 0.75 } }} />
                      </TableCell>
                    )}
                    {columns.map((col) => {
                      const isZero = col.kind !== 'tar' && !Number(c[col.key]);
                      return (
                        <TableCell
                          key={col.key}
                          align="center"
                          sx={{ whiteSpace: 'nowrap', fontWeight: col.bold ? 800 : 600, color: isZero ? '#CBD5E1' : col.color }}
                        >
                          {cellValue(col, c, { userIds: [c.userId], projectKey: c.projectKey }, c.name)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              };

              if (!groups) return rows.map(croRow);
              return groups.flatMap((g) => {
                const pc = projectColor(g.projectKey);
                const isCollapsed = collapsed && collapsed.has(g.key);
                // a team band is a calm light blue (like the rest of the page); the project shows in its coloured edge and chip
                const band = { backgroundColor: THEME.primarySoft, color: THEME.primaryDark, fontWeight: 800, cursor: 'pointer' };
                const header = (
                  <TableRow key={`team-${g.key}`} onClick={() => onToggleGroup && onToggleGroup(g.key)} sx={{ '&:hover td': { filter: 'brightness(0.97)' } }}>
                    <TableCell align="center" sx={{ ...stickySl, ...band, borderLeft: `4px solid ${pc.main}`, px: 0 }}>
                      {isCollapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
                    </TableCell>
                    <TableCell sx={{ ...stickyName, left: sl, ...band }}>
                      {g.teamId === 0 ? 'No CRM' : `Team ${g.teamName}`}
                      <Chip
                        label={`${croCount(g.rows)} ${croCount(g.rows) === 1 ? 'CRO' : 'CROs'}`}
                        size="small"
                        sx={{ ml: 1, height: 18, fontSize: 11, fontWeight: 700, color: '#fff', backgroundColor: pc.main }}
                      />
                      {g.teamId !== 0 && !g.headActive && (
                        <Chip label="CRM inactive" size="small" sx={{ ml: 0.75, height: 18, fontSize: 11, fontWeight: 600, color: '#9A3412', backgroundColor: '#FFEDD5' }} />
                      )}
                    </TableCell>
                    {projectCol && (
                      <TableCell align="center" sx={band}>
                        {g.project}
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.key} align="center" sx={{ ...band, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {cellValue(
                          col,
                          g.total,
                          { userIds: g.rows.map((r) => r.userId), projectKey: g.projectKey },
                          g.teamId === 0 ? 'No CRM' : `Team ${g.teamName}`,
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
                return isCollapsed ? [header] : [header, ...g.rows.map(croRow)];
              });
            })()}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Box>
  );
}
